/* eslint-disable no-console */
import { getData } from 'helpers/dom'
import './carousel-service.css'
import type { CarouselOptions } from './types'
import { calculateSlideDimensions } from './carousel-helpers'
import { detectBreakpoint } from 'helpers/breakpoint'

const SCROLL_SPEED = 1
const DRAG_THRESHOLD = 5

interface CarouselConfig {
  scrollbar?: boolean
  mobileOverride?: boolean
  [key: string]: unknown
}

interface ScrubState {
  dragging: boolean
  pointerId: number | null
}

class CarouselBlock extends HTMLElement {
  // DOM element refs
  private scrollerOuterRef: HTMLElement | null = null
  private scrolllerCarouselRef: HTMLElement | null = null
  private scrollerInnerRef: HTMLElement | null = null
  private scrubRef: HTMLElement | null = null
  private trackRef: HTMLElement | null = null
  private thumbRef: HTMLElement | null = null

  // State tracking
  private isOverScroller: boolean = false
  private isLeftMouseDown: boolean = false
  private scrollLeftPosition: number = 0
  private startX: number = 0
  private scrubState: ScrubState = {
    dragging: false,
    pointerId: null
  }

  private didDrag: boolean = false
  private lastScrubState: boolean | null = null

  // Configuration
  private config: CarouselConfig | null = null
  private options: CarouselOptions | null = null
  private carouselId: string | null = null

  // Observers and animation
  private resizeObserver: ResizeObserver | null = null
  private trackResizeObserver: ResizeObserver | null = null

  constructor() {
    super()
    this.handleScroll = this.handleScroll.bind(this)
    this.handleCarouselPointerDown = this.handleCarouselPointerDown.bind(this)
    this.handleMouseMove = this.handleMouseMove.bind(this)
    this.handleClickCapture = this.handleClickCapture.bind(this)
    this.handleMouseEnter = this.handleMouseEnter.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.handlePointerDownGlobal = this.handlePointerDownGlobal.bind(this)
    this.handlePointerUpGlobal = this.handlePointerUpGlobal.bind(this)
    this.handleScrubPointerDown = this.handleScrubPointerDown.bind(this)
    this.handleScrubPointerMove = this.handleScrubPointerMove.bind(this)
    this.handleScrubPointerUp = this.handleScrubPointerUp.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.measure = this.measure.bind(this)
    this.updateThumb = this.updateThumb.bind(this)
    this.setScrollFromClientX = this.setScrollFromClientX.bind(this)
  }

  connectedCallback() {
    this.initializeCarousel()
  }

  disconnectedCallback() {
    this.cleanup()
  }

  private initializeCarousel(): void {
    this.config = this.getAttribute('data-config') ? JSON.parse(getData('config', this)) : null
    this.options = this.getAttribute('data-options') ? JSON.parse(getData('options', this)) : null
    this.carouselId = this.getAttribute('data-id')

    this.scrollerOuterRef = document.querySelector(`#carousel-service-outer-${this.carouselId}`) as HTMLElement | null
    this.scrollerInnerRef = this.querySelector(`#carousel-service-inner-${this.carouselId}`) as HTMLElement | null
    this.scrolllerCarouselRef = this

    if (!this.scrollerOuterRef || !this.scrolllerCarouselRef || !this.scrollerInnerRef) {
      // eslint-disable-next-line no-console
      console.warn('CarouselBlock: Scroller elements not found', {
        outer: this.scrollerOuterRef,
        carousel: this.scrolllerCarouselRef,
        inner: this.scrollerInnerRef
      })

      return
    }

    this.scrubRef = document.querySelector(`#scrub-${this.carouselId}`) as HTMLElement | null

    if (!this.scrubRef && this.config?.scrollbar !== false) {
      // eslint-disable-next-line no-console
      console.warn('CarouselBlock: Scrub element not found', `#scrub-${this.carouselId}`)

      return
    }

    if (this.scrubRef) {
      this.trackRef = this.scrubRef.querySelector(`#scrub__track-${this.carouselId}`) as HTMLElement | null
      this.thumbRef = this.scrubRef.querySelector(`#scrub__thumb-${this.carouselId}`) as HTMLElement | null

      if (!this.trackRef || !this.thumbRef) {
        // eslint-disable-next-line no-console
        console.warn('CarouselBlock: Scrub track or thumb not found', {
          track: `#scrub__track-${this.carouselId}`,
          thumb: `#scrub__thumb-${this.carouselId}`
        })
      }
    }

    const swiperSlides = this.scrollerInnerRef.querySelectorAll('swiper-slide, .swiper-slide')

    swiperSlides.forEach((slide) => {
      ;(slide as HTMLElement).style.flexShrink = '0'
    })

    this.setupEventListeners()
    this.measure()

    // Apply responsive spacing after DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.applySlideSpacing()
      })
    })
  }

  private measure(): void {
    if (!this.scrollerInnerRef || !this.trackRef || !this.thumbRef) return

    const scroller = this.scrollerInnerRef
    const track = this.trackRef
    const thumb = this.thumbRef

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth)
    const trackW = track.getBoundingClientRect().width
    const thumbW = maxScroll === 0 ? trackW : trackW * (scroller.clientWidth / scroller.scrollWidth)

    thumb.style.width = `${Math.max(24, thumbW)}px`
    this.updateThumb(maxScroll, trackW, thumbW)
  }

  private updateThumb(maxScroll: number, trackW: number, thumbW: number): void {
    if (!this.scrollerInnerRef || !this.scrubRef || !this.thumbRef) return

    const scroller = this.scrollerInnerRef
    const scrub = this.scrubRef
    const thumb = this.thumbRef

    const progress = maxScroll === 0 ? 0 : scroller.scrollLeft / maxScroll
    const maxLeft = Math.max(0, trackW - thumbW)

    thumb.style.left = `${progress * maxLeft}px`
    scrub.setAttribute('aria-valuenow', Math.round(progress * 100).toString())

    const hasOverflow = maxScroll > 0
    const shouldShowScrub = hasOverflow

    if (this.lastScrubState !== shouldShowScrub) {
      this.lastScrubState = shouldShowScrub

      if (shouldShowScrub) {
        scrub.style.display = 'block'
        scroller.style.justifyContent = ''
      } else {
        scrub.style.display = 'none'
        scroller.style.justifyContent = 'center'
      }
    }
  }

  private setScrollFromClientX(clientX: number): void {
    if (!this.trackRef || !this.scrollerInnerRef) return

    const track = this.trackRef
    const scroller = this.scrollerInnerRef
    const rect = track.getBoundingClientRect()

    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth)
    const trackW = rect.width
    const thumbW = maxScroll === 0 ? trackW : trackW * (scroller.clientWidth / scroller.scrollWidth)

    const x = clientX - rect.left - thumbW / 2
    const p = Math.min(1, Math.max(0, x / (rect.width - thumbW)))

    scroller.scrollTo({ left: p * maxScroll, behavior: 'auto' })
  }

  private handleScroll(): void {
    requestAnimationFrame(() => {
      this.measure()
    })
  }

  private handleMouseEnter(): void {
    this.isOverScroller = true
  }

  private handleMouseLeave(): void {
    this.isOverScroller = false
    this.scrollerInnerRef?.classList?.remove('dragging')
  }

  private handlePointerDownGlobal(e: PointerEvent): void {
    if (e.button === 0) this.isLeftMouseDown = true
  }

  private handlePointerUpGlobal(e: PointerEvent): void {
    if (e.button === 0) this.isLeftMouseDown = false
  }

  private handleCarouselPointerDown(e: PointerEvent): void {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    if (!this.scrollerInnerRef) return
    this.didDrag = false
    this.startX = e.pageX - this.scrollerInnerRef.offsetLeft
    this.scrollLeftPosition = this.scrollerInnerRef.scrollLeft
    this.scrollerInnerRef.classList.add('dragging')
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.isOverScroller || !this.scrollerInnerRef || !this.isLeftMouseDown) {
      this.scrollerInnerRef?.classList?.remove('dragging')

      return
    }

    const x = e.pageX - this.scrollerInnerRef.offsetLeft
    const walk = (x - this.startX) * SCROLL_SPEED

    if (Math.abs(x - this.startX) > DRAG_THRESHOLD) {
      this.didDrag = true
    }

    this.scrollerInnerRef.scrollLeft = this.scrollLeftPosition - walk
  }

  private handleClickCapture(e: MouseEvent): void {
    if (!this.didDrag) return

    const target = e.target as HTMLElement

    if (target.closest('a, button')) {
      e.preventDefault()
      e.stopPropagation()

      if (e.stopImmediatePropagation) {
        e.stopImmediatePropagation()
      }
    }

    this.didDrag = false
  }

  private handleScrubPointerDown(e: PointerEvent): void {
    if (e.button !== 0) return

    this.scrubState.dragging = true
    this.scrubState.pointerId = e.pointerId

    if (this.scrubRef) {
      this.scrubRef.classList.add('dragging')
      this.scrubRef.setPointerCapture(e.pointerId)
    }

    this.setScrollFromClientX(e.clientX)
  }

  private handleScrubPointerMove(e: PointerEvent): void {
    if (!this.scrubState.dragging) return
    this.setScrollFromClientX(e.clientX)
  }

  private handleScrubPointerUp(): void {
    const pointerId = this.scrubState.pointerId

    this.scrubState.dragging = false
    this.scrubState.pointerId = null

    if (this.scrubRef) {
      this.scrubRef.classList.remove('dragging')

      if (pointerId !== null) {
        this.scrubRef.releasePointerCapture(pointerId)
      }
    }
  }

  private handleResize(): void {
    this.measure()
    this.applySlideSpacing()
  }

  private applySlideSpacing(): void {
    if (!this.scrollerInnerRef || !this.scrollerOuterRef || !this.options) return

    let outerWidth = this.scrollerOuterRef.clientWidth
    let scrollWidth = this.scrollerInnerRef.scrollWidth

    // Reuse first visible blog carousel dimensions when this one is in a hidden tab (same layout)
    // Did not want to have to do this... But they will both have the same amount of cards.
    if (this.carouselId === 'carousel-swiper-blog-carousel-2' && outerWidth <= 0) {
      const fallbackOuter = document.querySelector('#carousel-service-outer-carousel-swiper-blog-carousel-1') as HTMLElement | null
      const fallbackInner = document.querySelector('#carousel-service-inner-carousel-swiper-blog-carousel-1') as HTMLElement | null

      if (fallbackOuter && fallbackInner) {
        outerWidth = fallbackOuter.clientWidth
        scrollWidth = fallbackInner.scrollWidth
      }
    }

    const slideDimensions = calculateSlideDimensions(this.options, outerWidth, scrollWidth)

    if (!slideDimensions) return

    const isMobile = detectBreakpoint('', window.innerWidth)

    const swiperSlides = this.scrollerInnerRef.querySelectorAll(
      'swiper-slide, .swiper-slide'
    )

    swiperSlides.forEach((slide) => {
      const slideElement = slide as HTMLElement

      // Apply width if specified (for fixed slidesPerView)
      if (slideDimensions.width && slideDimensions.width !== 'auto') {
        slideElement.style.width = slideDimensions.width
      }

      // Apply marginRight for spacing: always on desktop, or on mobile only if mobileOverride is true
      if (slideDimensions.marginRight && (this.options?.mobileOverride || !isMobile)) {
        slideElement.style.marginRight = slideDimensions.marginRight
      }
    })
  }

  private setupEventListeners(): void {
    if (!this.scrollerInnerRef) return

    const scroller = this.scrollerInnerRef
    const scrub = this.scrubRef

    scroller.addEventListener('scroll', this.handleScroll, { passive: true })
    scroller.addEventListener('pointerdown', this.handleCarouselPointerDown)
    scroller.addEventListener('mouseenter', this.handleMouseEnter)
    scroller.addEventListener('mouseleave', this.handleMouseLeave)
    scroller.addEventListener('click', this.handleClickCapture, true)
    window.addEventListener('mousemove', this.handleMouseMove)
    window.addEventListener('pointerdown', this.handlePointerDownGlobal)
    window.addEventListener('pointerup', this.handlePointerUpGlobal)
    window.addEventListener('pointercancel', this.handlePointerUpGlobal)
    window.addEventListener('resize', this.handleResize)

    if (scrub) {
      scrub.addEventListener('pointerdown', this.handleScrubPointerDown)
      scrub.addEventListener('pointermove', this.handleScrubPointerMove)
      scrub.addEventListener('pointerup', this.handleScrubPointerUp)
      scrub.addEventListener('pointercancel', this.handleScrubPointerUp)
      scrub.addEventListener('lostpointercapture', this.handleScrubPointerUp)
    }

    if (this.scrollerInnerRef) {
      this.resizeObserver = new ResizeObserver(() => {
        this.measure()
      })
      this.resizeObserver.observe(this.scrollerInnerRef)
    }

    if (this.trackRef) {
      this.trackResizeObserver = new ResizeObserver(() => {
        this.measure()
      })
      this.trackResizeObserver.observe(this.trackRef)
    }
  }

  private cleanup(): void {
    if (!this.scrollerInnerRef) return

    const scroller = this.scrollerInnerRef
    const scrub = this.scrubRef

    scroller.removeEventListener('scroll', this.handleScroll)
    scroller.removeEventListener('pointerdown', this.handleCarouselPointerDown)
    scroller.removeEventListener('mouseenter', this.handleMouseEnter)
    scroller.removeEventListener('mouseleave', this.handleMouseLeave)
    scroller.removeEventListener('click', this.handleClickCapture, true)
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('pointerdown', this.handlePointerDownGlobal)
    window.removeEventListener('pointerup', this.handlePointerUpGlobal)
    window.removeEventListener('pointercancel', this.handlePointerUpGlobal)
    window.removeEventListener('resize', this.handleResize)

    if (scrub) {
      scrub.removeEventListener('pointerdown', this.handleScrubPointerDown)
      scrub.removeEventListener('pointermove', this.handleScrubPointerMove)
      scrub.removeEventListener('pointerup', this.handleScrubPointerUp)
      scrub.removeEventListener('pointercancel', this.handleScrubPointerUp)
      scrub.removeEventListener('lostpointercapture', this.handleScrubPointerUp)
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    if (this.trackResizeObserver) {
      this.trackResizeObserver.disconnect()
    }
  }
}

if (!customElements.get('carousel-block')) {
  customElements.define('carousel-block', CarouselBlock)
}
